import React, { useEffect, useRef, useState } from "react";
import NumberFormat from "react-number-format";
import { Link, useHistory } from "react-router-dom";
import { Button, Confirm, Icon, Modal } from "semantic-ui-react";
import { connect } from "react-redux";
import { ClientModel } from "../../entities/client.model";
import store from "../../store";
import { parsePaymentMethod, parseStatus } from "../../utils/shared.utils";
import { orderAddressText } from "../../utils/orderDelivery";
import { generateReceiptHTML, ReceiptData } from "../../utils/receiptGenerator";
import "./client.orders.scss";

interface OrdersProps {
  orders: any[];
  showStaff: boolean;
  onToggle?: (index: number) => void;
  visible?: boolean;
  setVisible?: React.Dispatch<React.SetStateAction<boolean>>;
  products: any[];
  basketHasItems: boolean;
}

const bucket = process.env.REACT_APP_S3_BUCKET;
const region = process.env.REACT_APP_S3_REGION;

/** Formatted delivery day strings from client.orders / company.orders after fetch. */
function deliveryDatesSummary(order: any): string {
  const dates = (order.deliveries || [])
    .map((d: any) => d.date)
    .filter(Boolean);
  return Array.from(new Set(dates)).join(", ");
}

const mapStateToProps = (state: any) => ({
  products: state.products || [],
  basketHasItems: Object.values(state.basket?.quantityById || {}).some((q: any) => Number(q) > 0),
});

async function fetchMyDetails(): Promise<ClientModel> {
  const token = localStorage.getItem("clientToken");
  if (!token || token === "undefined") {
    throw new Error("Not authenticated");
  }

  const res = await fetch(`${process.env.REACT_APP_API_URL}client`, {
    method: "GET",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: token,
    },
  });

  if (!res.ok) {
    const err = (await res.json().catch(() => ({}))) as any;
    throw new Error(err.errorMsg || `HTTP ${res.status}`);
  }

  const payload = (await res.json()) as { client: ClientModel };
  return payload.client;
}

function OrderDetailsComponent(props: OrdersProps) {
  const { showStaff, onToggle, visible, setVisible } = props;
  const history = useHistory();
  const [client, setClient] = useState<ClientModel | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [pendingEditOrder, setPendingEditOrder] = useState<any | null>(null);
  const [receiptPrintedOrder, setReceiptPrintedOrder] = useState<any | null>(null);
  const [pendingReorder, setPendingReorder] = useState<any | null>(null);
  const [reorderingId, setReorderingId] = useState<number | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    fetchMyDetails()
      .then((c) => {
        if (!isMountedRef.current) return;
        setClient(c);
      })
      .catch(() => {
        if (!isMountedRef.current) return;
        setErrorMsg("Алдаа гарлаа");
      })
      .finally(() => {
        if (!isMountedRef.current) return;
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    setOrders(props.orders);
  }, [props.orders]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [orders, setOrders] = useState<any[]>([]);
  const [editingOrderId, setEditingOrderId] = useState<number | null>(null);

  const normalizeProducts = (products: any[]) => {
    return products.map((p: any) => {
      const normalized = { ...p };
      if (normalized.saleActive === 1 && normalized.saleUntil) {
        let saleUntil = new Date(normalized.saleUntil);
        saleUntil.setDate(saleUntil.getDate() + 1);
        const today = new Date();
        normalized.saleActive = saleUntil >= today;
      } else {
        normalized.saleActive = false;
      }
      normalized.active = normalized.active === 1;
      normalized.usages = normalized.usages ? String(normalized.usages).split(",") : [];
      normalized.types = normalized.types ? String(normalized.types).split(",") : [];
      normalized.imageLgUrl = `https://${bucket}.s3.${region}.amazonaws.com/${normalized.imageLgUrl}`;
      normalized.imageSmUrl = `https://${bucket}.s3.${region}.amazonaws.com/${normalized.imageSmUrl}`;
      normalized.imageXsUrl = `https://${bucket}.s3.${region}.amazonaws.com/${normalized.imageXsUrl}`;
      if (normalized.image1) normalized.image1 = `https://${bucket}.s3.${region}.amazonaws.com/${normalized.image1}`;
      if (normalized.image2) normalized.image2 = `https://${bucket}.s3.${region}.amazonaws.com/${normalized.image2}`;
      if (normalized.image3) normalized.image3 = `https://${bucket}.s3.${region}.amazonaws.com/${normalized.image3}`;
      return normalized;
    });
  };

  const fetchLatestProducts = async () => {
    const res = await fetch(`${process.env.REACT_APP_API_URL}products`);
    if (!res.ok) {
      throw new Error("Products refresh failed");
    }
    const resJson = await res.json();
    const latestProducts = normalizeProducts(resJson?.data || []);
    store.dispatch({
      type: "products",
      data: latestProducts,
    });
    return latestProducts;
  };

  /**
   * Replaces the basket with the order's items. Products that are no longer sold
   * (missing from latestProducts) are skipped and returned by name. The basket is
   * left untouched when nothing from the order can be added.
   */
  const fillBasketFromOrder = (order: any, latestProducts: any[]) => {
    const productMap = new Map<number, any>();
    for (const product of latestProducts) {
      productMap.set(product.id, product);
    }

    const quantityMap = new Map<number, number>();
    const names = new Map<number, string>();
    for (const delivery of order.deliveries || []) {
      for (const item of delivery.items || []) {
        const productId = Number(item?.product?.id);
        if (!productId) continue;
        quantityMap.set(productId, (quantityMap.get(productId) || 0) + Number(item.count || 0));
        names.set(productId, item.product.name || String(productId));
      }
    }

    const toAdd: { product: any; count: number }[] = [];
    const skipped: string[] = [];
    quantityMap.forEach((count: number, productId: number) => {
      const product = productMap.get(productId);
      if (!product || count <= 0) {
        skipped.push(names.get(productId) || String(productId));
        return;
      }
      toAdd.push({ product, count });
    });

    if (toAdd.length > 0) {
      store.dispatch({ type: "clearBasket" });
      for (const { product, count } of toAdd) {
        store.dispatch({ type: "updateBasketItemCount", product, count });
      }
    }
    return { addedCount: toAdd.length, skipped };
  };

  const restoreOrderItemsToBasket = (order: any, latestProducts: any[]) => {
    const { addedCount } = fillBasketFromOrder(order, latestProducts);
    if (addedCount === 0) {
      // the original order is already cancelled, so never leave a stale basket behind
      store.dispatch({ type: "clearBasket" });
    }

    const preferredCompanyId = Number(order?.companyId ?? order?.company?.id);
    if (!Number.isNaN(preferredCompanyId) && preferredCompanyId > 0) {
      store.dispatch({
        type: "basketAddress",
        data: {
          town: order.town || "",
          district: order.district || "",
          unit: order.unit || "",
          street: order.street || "",
        },
        value: preferredCompanyId,
      });
    }
  };

  const editOrder = async (order: any, e?: any) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    const token = localStorage.getItem("clientToken");
    if (!token || token === "undefined") {
      history.push("/login");
      return;
    }

    try {
      if (!isMountedRef.current) return;
      setErrorMsg("");
      setEditingOrderId(order.id);

      const res = await fetch(`${process.env.REACT_APP_API_URL}client/order/edit/${order.id}`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          Authorization: token,
          "Content-Type": "application/json",
        },
      });

      if (!res.ok) {
        if (res.status === 409) {
          // Receipt may have been printed after this list was loaded
          const body = await res.json().catch(() => null);
          if (body?.code === "RECEIPT_PRINTED") {
            if (isMountedRef.current) {
              const printedOrder = {
                ...order,
                receiptCount: Math.max(Number(order.receiptCount) || 0, 1),
              };
              setOrders((prev) => prev.map((o) => (o.id === order.id ? printedOrder : o)));
              setReceiptPrintedOrder(printedOrder);
            }
            return;
          }
        }
        if (res.status === 404) {
          if (isMountedRef.current) {
            setErrorMsg("Захиалга өөрчлөх боломжгүй байна.");
          }
          return;
        }
        if (isMountedRef.current) {
          setErrorMsg("Захиалга цуцлахад алдаа гарлаа.");
        }
        return;
      }

      const latestProducts = await fetchLatestProducts();
      restoreOrderItemsToBasket(order, latestProducts);
      history.push("/client/checkout");
    } catch (err) {
      if (isMountedRef.current) {
        setErrorMsg("Сүлжээний алдаа");
      }
      console.error(err);
    } finally {
      if (isMountedRef.current) {
        setEditingOrderId(null);
      }
    }
  };

  // Regular (non-superuser) clients: copy a past order into the basket. Unlike editOrder,
  // the original order is left as is.
  const reorder = async (order: any) => {
    try {
      setErrorMsg("");
      setReorderingId(order.id);

      const latestProducts = await fetchLatestProducts();
      const { addedCount, skipped } = fillBasketFromOrder(order, latestProducts);
      if (!isMountedRef.current) return;

      if (addedCount === 0) {
        setErrorMsg("Захиалгын бараанууд одоогоор зарагдахгүй байна.");
        return;
      }
      if (skipped.length > 0) {
        // stay here so the user sees which items were left out before checking out
        setErrorMsg(`Дараах бараа зарагдахгүй тул сагсанд нэмэгдсэнгүй: ${skipped.join(", ")}. Сагсаа шалгана уу.`);
        window.scrollTo(0, 0);
        return;
      }
      history.push("/client/checkout");
    } catch (err) {
      if (isMountedRef.current) {
        setErrorMsg("Сүлжээний алдаа");
      }
      console.error(err);
    } finally {
      if (isMountedRef.current) {
        setReorderingId(null);
      }
    }
  };

  const toggle = (index: number) => {
    let ordersCopy = [...orders];
    ordersCopy[index].showItems = !ordersCopy[index].showItems;
    setOrders(ordersCopy);
    props.setVisible?.(false);
  };

  const printReceipt58mm = (order: any) => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    const items = (order.deliveries || []).flatMap((d: any) => d?.items || []).map((item: any) => ({
      name: item.product?.name + (item.product?.size ? ` (${item.product.size} ${item.product.unit || ""})` : ""),
      count: item.count,
      price: item.price,
      saleActive: false,
      salePrice: item.price,
    }));

    const receiptData: ReceiptData = {
      businessName: "Шеф Хоул Сайл",
      taxId: "123123",
      employeeName: order.createdBy || client?.ownName || "",
      paymentMethod: parsePaymentMethod(order.method) || "",
      companyRegister: order.company?.register ? order.company.register : "",
      phone: order.company?.register ? "" : order.phone || "",
      orderToken: order.token,
      items,
    };

    const receipt = generateReceiptHTML(receiptData);
    printWindow.document.write(receipt);
    printWindow.focus();
    printWindow.print();
  };

  return (
    <React.Fragment>
      {errorMsg ? <div className="width-100 text-center error-msg">
        <Icon name="warning circle" />{errorMsg}
      </div> : null}
      {orders?.map((order: any, index: number) => {
        const deliveryDates = deliveryDatesSummary(order);
        const currentClientId = Number(client?.id);
        const orderClientId = Number(order?.clientId);
        const isOwnOrder = Number.isFinite(currentClientId) && currentClientId > 0
          ? Number.isFinite(orderClientId) && orderClientId === currentClientId
          : false;
        return (
          <div className="client-order" key={"order-" + index}>
            {visible && (
              <div
                className={`order-top ${String(
                  order.status || ""
                ).toLowerCase()}`}
                onClick={() => toggle(index)}
              >
                <div className="order-col order-id fontMain-2">
                  <div className="cursor">
                    <p>№</p>
                    <strong>{order.token}</strong>
                  </div>
                </div>
                {props.showStaff && order.orderingCompanyName ? (
                  <div className="order-col cursor">
                    <p>Байгууллага</p>
                    <div className="order-method" title={order.orderingCompanyName}>
                      {order.orderingCompanyName}
                    </div>
                  </div>
                ) : null}
                {order.store === 1 ?
                  <div className="order-col cursor">
                    <p>Дэлгүүр</p>
                    <div className="order-method">Төлбөрийн хэрэгсэл: {parsePaymentMethod(order.method)}</div>
                    {order.phone ? <div className="order-method">Утас:{order.phone}</div> : null}
                    {order.company.name ? <div className="order-method">Байгууллага: {order.company.name}, {order.company.register}</div> : null}
                  </div>
                  : null}
                <div className="order-col cursor">
                  <p>Төлөв</p>
                  <div
                    className={`order-status ${String(
                      order.status || ""
                    ).toLowerCase()}`}
                  >
                    {parseStatus(order.status)}({order.createdAtStr})
                  </div>
                </div>
                {deliveryDates ? (
                  <div className="order-col cursor">
                    <p>Хүргэх өдөр</p>
                    <div className="order-delivery-date-summary">
                      {deliveryDates}
                    </div>
                  </div>
                ) : null}
                {order.express ? (
                  <div className="order-col cursor" data-testid={`client-order-express-${order.id}`}>
                    <p>Express</p>
                    <div className="order-method">{order.expressZone}</div>
                  </div>
                ) : null}
                {order.pickup ? (
                  <div className="order-col cursor" data-testid={`client-order-pickup-${order.id}`}>
                    <p>Хүлээн авах</p>
                    <div className="order-method">Очиж авна</div>
                  </div>
                ) : null}
                <div className="order-col order-amount cursor">
                  {/* <div className="col-top">Нийт дүн</div> */}
                  <p>Захиалгын дүн</p>
                  <NumberFormat
                    displayType={"text"}
                    value={order.totalAmount}
                    suffix="₮"
                    thousandSeparator={true}
                  />
                </div>
                <div className="order-col cursor">
                  <p>Баримт хэвлэлт</p>
                  <div className="order-method">
                    Хэвлэсэн: {Number(order.receiptCount) || 0}
                  </div>
                </div>
                {client?.superUser === 1 && (
                  <div className="order-col order-print-col">
                    {order.status === "CONFIRMED" ? (
                      <React.Fragment>
                        <Button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            history.push(`/client/order-receipt/${order.token}`);
                          }}
                        >
                          <Icon name="print" />
                          Баримт хэвлэх
                        </Button>
                        <Button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            printReceipt58mm(order);
                          }}
                        >
                          <Icon name="print" />
                          Баримт хэвлэх (58mm)
                        </Button>
                      </React.Fragment>
                    ) : null}
                    {(order.status === "CONFIRMED" || order.status === "PLACED") && order.store !== 1 && isOwnOrder ? (
                      <Button
                        type="button"
                        loading={editingOrderId === order.id}
                        disabled={editingOrderId !== null}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (Number(order.receiptCount) > 0) {
                            setReceiptPrintedOrder(order);
                          } else {
                            setPendingEditOrder(order);
                          }
                        }}
                      >
                        Захиалга өөрчлөх
                      </Button>
                    ) : null}
                  </div>
                )}
                {client && client.superUser !== 1 && order.store !== 1 && isOwnOrder ? (
                  <div className="order-col order-print-col">
                    <Button
                      type="button"
                      loading={reorderingId === order.id}
                      disabled={reorderingId !== null}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (props.basketHasItems) {
                          setPendingReorder(order);
                        } else {
                          reorder(order);
                        }
                      }}
                    >
                      <Icon name="redo" />
                      Дахин захиалах
                    </Button>
                  </div>
                ) : null}
              </div>
            )}

            {!visible && order.showItems ? (
              <React.Fragment>
                <div className="order-items">
                  {order.status === "PLACED" ? (
                    <div className="pay-link">
                      <Link
                        className="underline"
                        to={"/client/payment/" + order.token}
                      >
                        Төлбөр төлөх заавар
                      </Link>
                    </div>
                  ) : null}

                  {props?.showStaff && order?.createdBy ? (
                    <div className="order-info">
                      <strong className="col-top">Захиалсан ажилтан: </strong>
                      <span>{order.createdBy}</span>
                    </div>
                  ) : null}

                  <div className="order-head">
                    <div className="order-info">
                      <strong className="col-top">Захиалгын дугаар</strong>
                      <h2>{order.token}</h2>
                    </div>
                    <div className="order-info">
                      <strong className="col-top">{order.createdAtStr}</strong>
                      <h2>{parseStatus(order.status)}</h2>
                    </div>
                    {deliveryDates ? (
                      <div className="order-info">
                        <strong className="col-top">Хүргэх өдөр</strong>
                        <h2 className="order-delivery-dates-head">
                          {deliveryDates}
                        </h2>
                      </div>
                    ) : null}
                    <div className="order-info">
                      <strong className="col-top">Баримт хэвлэлт</strong>
                      <h2>Хэвлэсэн: {Number(order.receiptCount) || 0}</h2>
                    </div>
                  </div>

                  {order.orderingCompanyName ? (
                    <div className="order-info">
                      <strong className="col-top">Захиалгын байгууллага</strong>
                      <div>{order.orderingCompanyName}</div>
                    </div>
                  ) : null}

                  {order.store === 1 ? (
                    <div className="order-sub-head">
                      <div className="order-info">
                        <strong className="col-top">Төлбөрийн хэрэгсэл</strong>
                        <div>{parsePaymentMethod(order.method)}</div>
                      </div>
                      {order.phone ? (
                        <div className="order-info">
                          <strong className="col-top">Утас</strong>
                          <div>{order.phone}</div>
                        </div>
                      ) : null}
                      {order.company.name ? (
                        <div className="order-info">
                          <strong className="col-top">Байгууллага</strong>
                          <div>{order.company.name}, {order.company.register}</div>
                        </div>
                      ) : null}
                    </div>
                  ) : null}

                  {/* <div className="order-address">
                    <strong className="col-top">Хаяг: </strong>
                    <span>
                      {order.town}, {order.district}, {order.unit},{" "}
                      {order.street}
                    </span>
                  </div> */}
                  <div className="orders-layout">
                    <div className="order-layout1">
                      <h4>Бүтээгдэхүүн</h4>
                    </div>
                    <div className="order-layout2">
                      <h4>Үнэ</h4>
                      <h4>Тоо, ширхэг</h4>
                      <h4>Нийт</h4>
                    </div>
                  </div>

                  {order.deliveries?.map((delivery: any, dindex: number) => {
                    return (
                      <div
                        className="order-delivery"
                        key={"delivery-" + delivery.id}
                      >
                        {delivery?.date ||
                        (delivery?.cost !== undefined &&
                          delivery?.cost !== null) ? (
                          <div className="order-delivery-top">
                            {delivery?.date ? (
                              <span className="delivery-date">
                                <Icon name="calendar outline" />
                                {delivery.date}
                              </span>
                            ) : null}
                            {delivery?.cost !== undefined &&
                            delivery?.cost !== null ? (
                              <span className="delivery-cost">
                                {delivery?.date ? " · " : null}
                                Хүргэлтийн үнэ:{" "}
                                <NumberFormat
                                  displayType="text"
                                  value={delivery.cost}
                                  thousandSeparator={true}
                                  suffix="₮"
                                />
                              </span>
                            ) : null}
                          </div>
                        ) : null}

                        {delivery?.items?.map((order: any, index: number) => {
                          return (
                            <div className="orders-layout">
                              <div className="order-layout1">
                                <img
                                  src={order.product.imageXsUrl}
                                  height="60"
                                  width="60"
                                  className="order-image"
                                />
                                <Link to={`/product/${order.product.slug}`}>
                                  {order.product.name}
                                  {"("}
                                  {order.product.size
                                    ? +order.product.size
                                    : ""}
                                  {order.product.size ? order.product.unit : ""}
                                  {")"}
                                </Link>
                              </div>
                              <div className="order-layout2">
                                <NumberFormat
                                  displayType="text"
                                  value={order.price}
                                  suffix="₮"
                                  thousandSeparator={true}
                                />{" "}
                                <span>{order.count}</span>
                                <NumberFormat
                                  displayType="text"
                                  value={order.price * order.count}
                                  suffix="₮"
                                  thousandSeparator={true}
                                  className="strong-text"
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                  <div className="order-summary">
                    <div>
                      Барааны нийт үнэ:{" "}
                      <NumberFormat
                        displayType="text"
                        value={order.totalAmount - order.totalDeliveryCost}
                        suffix="₮"
                        thousandSeparator={true}
                        className="strong-text"
                      />
                    </div>
                    <div>
                      Хүргэлтийн нийт үнэ:{" "}
                      <NumberFormat
                        displayType="text"
                        value={order.totalDeliveryCost}
                        suffix="₮"
                        thousandSeparator={true}
                        className="strong-text"
                      />
                    </div>
                    {order.express ? (
                      <div data-testid={`client-order-express-detail-${order.id}`}>
                        Үүнээс Express хүргэлт ({order.expressZone}):{" "}
                        {Number(order.expressFee) > 0 ? (
                          <NumberFormat
                            displayType="text"
                            value={order.expressFee}
                            suffix="₮"
                            thousandSeparator={true}
                            className="strong-text"
                          />
                        ) : (
                          "үнэгүй"
                        )}
                      </div>
                    ) : null}
                    <div className="text">
                      Нийт үнэ:{" "}
                      <NumberFormat
                        displayType="text"
                        value={order.totalAmount}
                        suffix="₮"
                        thousandSeparator={true}
                        className="strong-text"
                        style={{ fontSize: "20px" }}
                      />
                    </div>
                  </div>
                </div>

                {order.store === 1 ? null : (
                  !isMobile ? (
                    <div className="order-delivery-info">
                      <h3>Хүргэлтийн мэдээлэл</h3>
                      <div className="order-delivery-layout">
                        <p>Хүргэлтийн хаяг:</p>
                        <p>Утас</p>
                        <p>И-мэйл хаяг</p>
                      </div>
                      <div className="order-delivery-layout">
                        <span>{orderAddressText(order)}</span>
                        <span>{client?.phone}</span>
                        <span>{client?.email}</span>
                      </div>
                    </div>
                  ) : (
                    <div className="order-delivery-info">
                      <h3>Хүргэлтийн мэдээлэл</h3>
                      <p>Хүргэлтийн хаяг:</p>
                      <span>{orderAddressText(order)}</span>
                      <div className="order-delivery-layout-mobile">
                        <div>
                          <p>Утас</p>
                          <span>{client?.phone}</span>
                        </div>
                        <div>
                          <p>И-мэйл хаяг</p>
                          <span>{client?.email}</span>
                        </div>
                      </div>
                    </div>
                  )
                )}
              </React.Fragment>
            ) : null}
            {!visible && order.showItems && order.deliveryNote ? (
              <div className="order-delivery-info" data-testid={`client-order-note-${order.id}`}>
                <h3>Тайлбар</h3>
                <p>{order.deliveryNote}</p>
              </div>
            ) : null}
          </div>
        );
      })}
      <Confirm
        open={Boolean(pendingEditOrder)}
        header="Анхааруулга"
        content="Захиалга өөрчилсөнөөр одоо байгаа захиалга цуцлагдана"
        cancelButton="Болих"
        confirmButton="Захиалга өөрчлөх"
        onCancel={() => setPendingEditOrder(null)}
        onConfirm={async (e) => {
          e?.preventDefault?.();
          if (pendingEditOrder) {
            await editOrder(pendingEditOrder);
          }
          if (isMountedRef.current) {
            setPendingEditOrder(null);
          }
        }}
      />
      <Confirm
        open={Boolean(pendingReorder)}
        header="Анхааруулга"
        content="Сагсанд байгаа бараанууд энэ захиалгын бараагаар солигдоно"
        cancelButton="Болих"
        confirmButton="Дахин захиалах"
        onCancel={() => setPendingReorder(null)}
        onConfirm={async (e) => {
          e?.preventDefault?.();
          const order = pendingReorder;
          setPendingReorder(null);
          if (order) {
            await reorder(order);
          }
        }}
      />
      <Modal
        size="mini"
        open={Boolean(receiptPrintedOrder)}
        onClose={() => setReceiptPrintedOrder(null)}
      >
        <Modal.Header>Анхааруулга</Modal.Header>
        <Modal.Content>
          Хүргэлтийн баримт хэвлэгдсэн захиалгыг өөрчлөх боломжгүй.
          <br />
          Захиалга №{receiptPrintedOrder?.token} (Хэвлэсэн: {Number(receiptPrintedOrder?.receiptCount) || 0})
        </Modal.Content>
        <Modal.Actions>
          <Button primary onClick={() => setReceiptPrintedOrder(null)}>
            Ойлголоо
          </Button>
        </Modal.Actions>
      </Modal>
    </React.Fragment>
  );
}

export default connect(mapStateToProps)(OrderDetailsComponent);
